/**
 * Política de Privacidade e proteção de dados (LGPD) — página pública, sem login: /privacidade.
 * Os dados da empresa (razão social, CNPJ, endereço e o e-mail do encarregado) ficam em LEGAL, abaixo.
 */
import type { ReactNode } from 'react';
import { Link } from 'react-router';
import { LoginLogo } from '../components/Brand.tsx';

const LEGAL = {
  company: '[RAZÃO SOCIAL DA BERYTUS]',
  cnpj: '[CNPJ]',
  address: '[ENDEREÇO COMPLETO]',
  dpoName: '[NOME DO ENCARREGADO]',
  dpoEmail: '[E-MAIL DO ENCARREGADO]',
  updated: '27 de outubro de 2026',
};

function Section({ n, title, children }: { n: number; title: string; children: ReactNode }) {
  return (
    <section style={{ display: 'grid', gap: 10 }}>
      <h2 style={{ margin: 0, fontSize: 19 }}>{n}. {title}</h2>
      {children}
    </section>
  );
}

export function PrivacyPage() {
  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg)', color: 'var(--text)', padding: '32px 16px 64px' }}>
      <article style={{ maxWidth: 820, margin: '0 auto', display: 'grid', gap: 26, lineHeight: 1.6, fontSize: 15.5 }}>
        <header style={{ display: 'grid', gap: 14 }}>
          <Link to="/login" aria-label="Berytus" style={{ justifySelf: 'start' }}><LoginLogo /></Link>
          <h1 style={{ margin: 0, fontSize: 30 }}>Política de Privacidade e Proteção de Dados</h1>
          <p className="muted" style={{ margin: 0 }}>
            Como a Berytus trata dados pessoais, em conformidade com a Lei Geral de Proteção de Dados Pessoais (Lei nº 13.709/2018 — LGPD).
            Última atualização: {LEGAL.updated}.
          </p>
        </header>

        <Section n={1} title="Quem somos">
          <p style={{ margin: 0 }}>
            A Berytus é uma plataforma de integração que conecta farmácias e lojas aos marketplaces e aplicativos de entrega (como Mercado Livre,
            Shopee e iFood): catálogo de produtos, publicação de anúncios, preço e estoque, pedidos, notas fiscais e etiquetas de envio.
            A plataforma é operada por <b>{LEGAL.company}</b>, inscrita no CNPJ sob o nº <b>{LEGAL.cnpj}</b>, com sede em {LEGAL.address} (“Berytus”).
          </p>
        </Section>

        <Section n={2} title="Nosso papel no tratamento dos dados">
          <ul style={{ margin: 0, paddingLeft: 20, display: 'grid', gap: 6 }}>
            <li>
              <b>Dados dos compradores dos marketplaces</b> (pedidos, entrega, nota fiscal): a Berytus atua como <b>operadora</b>. Quem decide sobre
              esses dados é a farmácia ou loja que vendeu (a <b>controladora</b>), que contrata a Berytus para receber os pedidos dos canais e
              repassá-los ao seu sistema de gestão (ERP), emitir a nota e despachar. Tratamos esses dados apenas conforme as instruções do cliente
              e para essas finalidades.
            </li>
            <li>
              <b>Dados de cadastro dos nossos clientes e de seus usuários</b> (quem acessa a plataforma): a Berytus atua como <b>controladora</b>.
            </li>
          </ul>
        </Section>

        <Section n={3} title="Quais dados tratamos">
          <ul style={{ margin: 0, paddingLeft: 20, display: 'grid', gap: 6 }}>
            <li><b>Usuários da plataforma:</b> nome, e-mail, telefone, cargo/perfil de acesso, senha (guardada apenas na forma de hash, nunca em texto) e registros de acesso e de ações na plataforma.</li>
            <li><b>Empresas clientes:</b> razão social, nome fantasia, CNPJ, endereço e dados das contas conectadas aos marketplaces.</li>
            <li>
              <b>Compradores dos marketplaces</b> (recebidos dos canais, por conta do cliente): nome, CPF ou CNPJ, endereço de entrega, telefone,
              e-mail quando o canal informa, itens comprados, valores, forma de pagamento e situação da entrega.
            </li>
            <li>
              <b>Receitas médicas</b> (vendas de medicamentos que exigem receita, quando o canal oferece): o arquivo da receita enviado pelo comprador
              e a decisão do farmacêutico. É um <b>dado pessoal sensível</b> (saúde), tratado somente para a validação farmacêutica exigida para a
              dispensação do medicamento.
            </li>
          </ul>
          <p style={{ margin: 0 }}>Não coletamos dados de cartão de pagamento: o pagamento é feito e processado pelo próprio marketplace.</p>
        </Section>

        <Section n={4} title="Para que usamos e com qual base legal">
          <ul style={{ margin: 0, paddingLeft: 20, display: 'grid', gap: 6 }}>
            <li><b>Processar os pedidos</b> (repassar ao ERP do cliente, emitir e enviar a nota fiscal, gerar a etiqueta e acompanhar a entrega): execução de contrato e cumprimento de obrigação legal e fiscal (LGPD, art. 7º, II e V).</li>
            <li><b>Validação de receitas</b> pelo farmacêutico da farmácia: cumprimento de obrigação legal e regulatória sanitária (LGPD, art. 11, II, “a”).</li>
            <li><b>Prestar o serviço aos clientes</b> (acesso à plataforma, suporte, comunicações sobre a conta e cobrança): execução de contrato (art. 7º, V).</li>
            <li><b>Segurança da plataforma</b> e prevenção a fraudes e acessos indevidos: legítimo interesse (art. 7º, IX) e exercício regular de direitos (art. 7º, VI).</li>
          </ul>
          <p style={{ margin: 0 }}>
            <b>Não vendemos dados pessoais</b>, não os usamos para publicidade e não usamos os dados dos compradores para nenhuma finalidade própria
            da Berytus além de executar o serviço para o cliente.
          </p>
        </Section>

        <Section n={5} title="Com quem compartilhamos">
          <ul style={{ margin: 0, paddingLeft: 20, display: 'grid', gap: 6 }}>
            <li><b>A farmácia ou loja do pedido</b> e o sistema de gestão (ERP) que ela autorizar, por meio de chave de acesso exclusiva da empresa.</li>
            <li><b>O marketplace de origem do pedido</b> (Mercado Livre, Shopee, iFood), para executar a venda: envio da nota fiscal, etiqueta, situação da entrega e validação da receita.</li>
            <li><b>Prestadores de infraestrutura</b> que operam para a Berytus, sob dever de confidencialidade e segurança: hospedagem em nuvem, envio de e-mails transacionais e consulta de CEP (a consulta envia apenas o CEP, sem nenhum outro dado do comprador).</li>
            <li><b>Autoridades</b>, quando exigido por lei ou por ordem judicial.</li>
          </ul>
          <p style={{ margin: 0 }}>
            A hospedagem em nuvem pode envolver armazenamento em servidores localizados fora do Brasil; nesse caso, a transferência ocorre com as
            garantias previstas no art. 33 da LGPD.
          </p>
        </Section>

        <Section n={6} title="Como protegemos os dados">
          <ul style={{ margin: 0, paddingLeft: 20, display: 'grid', gap: 6 }}>
            <li><b>Conexões criptografadas</b> (HTTPS/TLS) em todo o acesso à plataforma e à API.</li>
            <li><b>Isolamento por empresa:</b> cada cliente só acessa os próprios dados; a separação é aplicada pelo próprio banco de dados, não apenas pelas telas.</li>
            <li><b>Controle de acesso por perfil:</b> cada usuário vê apenas o que o seu papel exige; as receitas médicas, por exemplo, são acessíveis apenas aos farmacêuticos e responsáveis da farmácia.</li>
            <li><b>Criptografia de dados críticos:</b> os arquivos de receitas e as credenciais de acesso aos marketplaces são guardados cifrados; senhas, apenas na forma de hash.</li>
            <li><b>Minimização:</b> CPF e telefone aparecem mascarados nas telas e não saem nas exportações de planilha.</li>
            <li><b>Rastreabilidade:</b> ações relevantes (como cancelamentos, decisões sobre receitas e alterações de acesso) ficam registradas.</li>
          </ul>
        </Section>

        <Section n={7} title="Por quanto tempo guardamos">
          <ul style={{ margin: 0, paddingLeft: 20, display: 'grid', gap: 6 }}>
            <li><b>Dados pessoais dos compradores:</b> apagados automaticamente 180 dias após a entrega ou o cancelamento do pedido. Os dados do pedido que não identificam o comprador (itens, valores) permanecem para os relatórios do cliente.</li>
            <li><b>Receitas médicas:</b> guardadas pelo prazo de guarda que a regulação sanitária exige da farmácia e, ao fim do contrato, eliminadas ou devolvidas ao cliente, conforme a instrução dele.</li>
            <li><b>Dados de cadastro e de acesso:</b> enquanto a conta estiver ativa e, depois, pelos prazos exigidos por lei (por exemplo, registros de acesso pelo prazo do Marco Civil da Internet).</li>
          </ul>
        </Section>

        <Section n={8} title="Direitos dos titulares">
          <p style={{ margin: 0 }}>
            O titular pode solicitar, nos termos do art. 18 da LGPD: confirmação do tratamento, acesso, correção, anonimização, bloqueio ou eliminação
            de dados desnecessários, portabilidade, informação sobre compartilhamento e revisão de decisões automatizadas.
          </p>
          <p style={{ margin: 0 }}>
            Como os dados dos compradores pertencem à farmácia ou loja que vendeu, os pedidos sobre esses dados são encaminhados a ela, e a Berytus
            apoia o cliente no atendimento. Pedidos sobre dados de cadastro na plataforma são atendidos diretamente pela Berytus.
          </p>
        </Section>

        <Section n={9} title="Incidentes de segurança">
          <p style={{ margin: 0 }}>
            Em caso de incidente de segurança que possa causar risco ou dano relevante aos titulares, a Berytus comunica os clientes afetados sem
            demora indevida, para que, como controladores, comuniquem a Autoridade Nacional de Proteção de Dados (ANPD) e os titulares, e apoia as
            medidas de contenção e comunicação.
          </p>
        </Section>

        <Section n={10} title="Encarregado (DPO) e contato">
          <p style={{ margin: 0 }}>
            Encarregado pelo tratamento de dados pessoais: <b>{LEGAL.dpoName}</b> — <a href={`mailto:${LEGAL.dpoEmail}`}>{LEGAL.dpoEmail}</a>.
            Use este contato para dúvidas sobre esta política ou para exercer os seus direitos.
          </p>
        </Section>

        <Section n={11} title="Alterações">
          <p style={{ margin: 0 }}>Esta política pode ser atualizada. A data da última atualização fica no topo desta página.</p>
        </Section>
      </article>
    </div>
  );
}
